import { useCallback, useMemo } from 'react';

import {
  resolveEmergencyCalleeButtonState,
  resolveEmergencyCalleePresentation,
} from './useEmergencyCallDetailsCallee.helpers';

import { InfoCircleIcon, MedicalCrossIcon } from 'assets/svg';
import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import { useCallHearingParticipantHandlers } from 'features/call/call-accepted/hooks';
import { updateCalleePhoneNumber } from 'features/call/call-base/store';
import {
  CallDetailsButtonCancel,
  CallDetailsButtonConnect,
  CallDetailsButtonDisconnect,
  CallDetailsButtonReconnect,
  CallDetailsExtensionInput,
  CallDetailsParticipantIcon,
} from 'features/call/call-details/components';
import { primaryHearingSelector } from 'features/call/call-hearing/store';
import { EmergencyNewCall } from 'features/emergency/components';
import {
  addCalledConnectionPoints,
  emergencyConnectionPointSelector,
  isCalledToConnectionPointSelector,
  isEmergencyCalleeReconnectSelector,
  isOtherConnectionPointSelector,
  selectShouldCancelHearingBeDisabledForEmergencyCall,
} from 'features/emergency/store';
import { shouldCallControlsBeVisibleSelector } from 'features/spawn-call/store';
import {
  isVoiceSessionLoadingSelector,
  voiceSessionStatusSelector,
} from 'features/voice-session/store';

export const useEmergencyCallDetailsCallee = () => {
  const dispatch = useAppDispatch();
  const { handleRegularHearingCall, handleHearingDisconnect } =
    useCallHearingParticipantHandlers();
  const hearing = useAppSelector(primaryHearingSelector);
  const emergencyConnectionPoint = useAppSelector(
    emergencyConnectionPointSelector
  );
  const voiceSessionStatus = useAppSelector(voiceSessionStatusSelector);
  const isVoiceSessionLoading = useAppSelector(isVoiceSessionLoadingSelector);
  const isEmergencyCalleeReconnect = useAppSelector(
    isEmergencyCalleeReconnectSelector
  );

  const isCalledToConnectionPoint = useAppSelector(
    isCalledToConnectionPointSelector
  );
  const isOtherConnectionPoint = useAppSelector(isOtherConnectionPointSelector);

  const handleStartCall = useCallback(async () => {
    await dispatch(updateCalleePhoneNumber());
    await handleRegularHearingCall(hearing.phoneNumber);

    if (emergencyConnectionPoint) {
      dispatch(addCalledConnectionPoints(emergencyConnectionPoint));
    }
  }, [
    dispatch,
    emergencyConnectionPoint,
    handleRegularHearingCall,
    hearing.phoneNumber,
  ]);

  const handleDisconnect = useCallback(() => {
    return handleHearingDisconnect(hearing.id);
  }, [handleHearingDisconnect, hearing.id]);

  const shouldHearingCancelBeDisabled = useAppSelector(
    selectShouldCancelHearingBeDisabledForEmergencyCall()
  );

  const shouldCallControlsBeVisible = useAppSelector(
    shouldCallControlsBeVisibleSelector
  );

  const calleeButtonState = resolveEmergencyCalleeButtonState({
    shouldCallControlsBeVisible,
    voiceSessionStatus,
    hearingStatus: hearing.status,
    isCalledToConnectionPoint,
    isEmergencyCalleeReconnect,
  });

  const calleeButton = useMemo(() => {
    switch (calleeButtonState) {
      case 'voice-session-cancel':
        return <CallDetailsButtonCancel loading />;

      case 'hearing-cancel':
        return (
          <CallDetailsButtonCancel
            loading={isVoiceSessionLoading || !hearing.id}
            onClick={handleDisconnect}
            disabled={shouldHearingCancelBeDisabled}
          />
        );

      case 'reconnect':
        return (
          <>
            <CallDetailsButtonReconnect onClick={handleStartCall} />
            <EmergencyNewCall />
          </>
        );

      case 'disconnect':
        return (
          <CallDetailsButtonDisconnect
            onClick={handleDisconnect}
            buttonType='hearing'
          />
        );

      case 'connect':
        return (
          <>
            <CallDetailsButtonConnect onClick={handleStartCall} />
            <EmergencyNewCall />
          </>
        );

      default:
        return null;
    }
  }, [
    calleeButtonState,
    handleDisconnect,
    handleStartCall,
    hearing.id,
    isVoiceSessionLoading,
    shouldHearingCancelBeDisabled,
  ]);

  const calleePresentation = resolveEmergencyCalleePresentation({
    emergencyConnectionPoint,
    isOtherConnectionPoint,
    isEmergencyCalleeReconnect,
    hearingStatus: hearing.status,
    isCalledToConnectionPoint,
  });

  const callee = useMemo(() => {
    const defaultMetadata = {
      children: calleeButton,
      testId: 'hearing-details-item',
    };

    switch (calleePresentation) {
      case 'ecrc':
        return {
          ...defaultMetadata,
          name: 'ECRC (Emergency Call Relay Center)',
          icon: (
            <InfoCircleIcon width={32} height={32} data-testid='info-circle' />
          ),
        };

      case 'psap':
        return {
          ...defaultMetadata,
          name: '911 Center',
          icon: (
            <MedicalCrossIcon
              width={32}
              height={32}
              title='Medical cross medium'
            />
          ),
        };

      case 'other':
        return {
          ...defaultMetadata,
          ...hearing,
          icon: <CallDetailsParticipantIcon isDeafParticipant={false} />,
          extensionInput: hearing.phoneExtension ? (
            <CallDetailsExtensionInput
              value={hearing.phoneExtension}
              disabled
              width='full'
            />
          ) : undefined,
        };

      default:
        return undefined;
    }
  }, [calleeButton, calleePresentation, hearing]);

  return { callee };
};
